import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { apiService } from '../services/api';
import {
  InventoryLocation,
  InventoryLocationCreate,
  InventoryLocationUpdate,
  InventoryItem,
  InventoryItemCreate,
  InventoryItemUpdate,
} from '../types/api';

interface InventoryState {
  locations: InventoryLocation[];
  items: InventoryItem[];
  selectedLocationId: string | null;
  loading: boolean;
  error: string | null;
  ongoingRequests: Set<string>;
}

interface InventoryActions {
  // Location actions
  loadLocations: () => Promise<void>;
  createLocation: (data: InventoryLocationCreate) => Promise<InventoryLocation | null>;
  updateLocation: (id: string, data: InventoryLocationUpdate) => Promise<InventoryLocation | null>;
  deleteLocation: (id: string) => Promise<boolean>;

  // Item actions
  loadItems: (locationId?: string, search?: string) => Promise<void>;
  createItem: (data: InventoryItemCreate) => Promise<InventoryItem | null>;
  updateItem: (id: string, data: InventoryItemUpdate) => Promise<InventoryItem | null>;
  deleteItem: (id: string) => Promise<boolean>;

  // UI state
  setSelectedLocationId: (id: string | null) => void;
  reset: () => void;
}

type InventoryStore = InventoryState & InventoryActions;

const initialState: InventoryState = {
  locations: [],
  items: [],
  selectedLocationId: null,
  loading: false,
  error: null,
  ongoingRequests: new Set<string>(),
};

export const useInventoryStore = create<InventoryStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // -------------------- locations --------------------

      loadLocations: async () => {
        const { ongoingRequests } = get();
        const key = 'load-locations';
        if (ongoingRequests.has(key)) return;
        ongoingRequests.add(key);

        set({ loading: true, error: null }, false, 'loadLocations-start');

        try {
          const locations = await apiService.getInventoryLocations();
          set({ locations, loading: false }, false, 'loadLocations-success');
        } catch (err) {
          const message = 'Failed to load inventory locations';
          console.error(message, err);
          set({ loading: false, error: message }, false, 'loadLocations-error');
        } finally {
          ongoingRequests.delete(key);
        }
      },

      createLocation: async (data) => {
        try {
          const location = await apiService.createInventoryLocation(data);
          set((state) => ({ locations: [...state.locations, location] }), false, 'createLocation');
          return location;
        } catch (err) {
          console.error('Failed to create location', err);
          set({ error: 'Failed to create location' }, false, 'createLocation-error');
          return null;
        }
      },

      updateLocation: async (id, data) => {
        try {
          const updated = await apiService.updateInventoryLocation(id, data);
          set(
            (state) => ({
              locations: state.locations.map((l) => (l.id === id ? updated : l)),
            }),
            false,
            'updateLocation'
          );
          // Reload items so location_name stays in sync
          get().loadItems(get().selectedLocationId ?? undefined);
          return updated;
        } catch (err) {
          console.error('Failed to update location', err);
          set({ error: 'Failed to update location' }, false, 'updateLocation-error');
          return null;
        }
      },

      deleteLocation: async (id) => {
        try {
          await apiService.deleteInventoryLocation(id);
          set(
            (state) => {
              // Remove the deleted location and all its descendants
              const idsToRemove = new Set<string>();
              const collectDescendants = (parentId: string) => {
                idsToRemove.add(parentId);
                state.locations
                  .filter((l) => l.parent_id === parentId)
                  .forEach((l) => collectDescendants(l.id));
              };
              collectDescendants(id);

              return {
                locations: state.locations.filter((l) => !idsToRemove.has(l.id)),
                // Clear selection if deleted location was selected
                selectedLocationId: idsToRemove.has(state.selectedLocationId ?? '')
                  ? null
                  : state.selectedLocationId,
              };
            },
            false,
            'deleteLocation'
          );
          // Reload items so orphaned items (location_id set to NULL by DB) update
          get().loadItems(get().selectedLocationId ?? undefined);
          return true;
        } catch (err) {
          console.error('Failed to delete location', err);
          set({ error: 'Failed to delete location' }, false, 'deleteLocation-error');
          return false;
        }
      },

      // -------------------- items --------------------

      loadItems: async (locationId?: string, search?: string) => {
        const { ongoingRequests } = get();
        const key = `load-items-${locationId ?? 'all'}-${search ?? ''}`;
        if (ongoingRequests.has(key)) return;
        ongoingRequests.add(key);

        set({ loading: true, error: null }, false, 'loadItems-start');

        try {
          const params: { location_id?: string; search?: string } = {};
          if (locationId) params.location_id = locationId;
          if (search) params.search = search;

          const items = await apiService.getInventoryItems(
            Object.keys(params).length > 0 ? params : undefined
          );
          set({ items, loading: false }, false, 'loadItems-success');
        } catch (err) {
          const message = 'Failed to load inventory items';
          console.error(message, err);
          set({ loading: false, error: message }, false, 'loadItems-error');
        } finally {
          ongoingRequests.delete(key);
        }
      },

      createItem: async (data) => {
        try {
          const item = await apiService.createInventoryItem(data);
          set((state) => ({ items: [...state.items, item] }), false, 'createItem');
          return item;
        } catch (err) {
          console.error('Failed to create item', err);
          set({ error: 'Failed to create item' }, false, 'createItem-error');
          return null;
        }
      },

      updateItem: async (id, data) => {
        try {
          const updated = await apiService.updateInventoryItem(id, data);
          set(
            (state) => ({
              items: state.items.map((i) => (i.id === id ? updated : i)),
            }),
            false,
            'updateItem'
          );
          return updated;
        } catch (err) {
          console.error('Failed to update item', err);
          set({ error: 'Failed to update item' }, false, 'updateItem-error');
          return null;
        }
      },

      deleteItem: async (id) => {
        try {
          await apiService.deleteInventoryItem(id);
          set((state) => ({ items: state.items.filter((i) => i.id !== id) }), false, 'deleteItem');
          return true;
        } catch (err) {
          console.error('Failed to delete item', err);
          set({ error: 'Failed to delete item' }, false, 'deleteItem-error');
          return false;
        }
      },

      // -------------------- UI state --------------------

      setSelectedLocationId: (id) => {
        set({ selectedLocationId: id }, false, 'setSelectedLocationId');
      },

      reset: () => {
        set({ ...initialState, ongoingRequests: new Set() }, false, 'reset');
      },
    }),
    { name: 'inventory-store' }
  )
);
